import {
  closeSessionAndPort,
  createSerialSession,
  OnSerialOutput,
  OnTerminalLog,
  SerialPortConnection,
} from './serial'
import { reconnect } from './reconnect'
import i18n from 'i18next'
import { storeCertificate, deleteCertificates } from './certificate'
import { checkModemAfterStore, ensureModemReady } from './modem'
import { switchToPassthroughMode } from './passThrough'

type UsbConnectionConfig = {
  vendorId: number
  productId: number
  baudRate: number
}

const throwIfAborted = (abortSignal: AbortSignal): void => {
  if (abortSignal.aborted) {
    throw new Error('upload aborted')
  }
}

const ensurePortStreamsAvailable = (port: SerialPortConnection): void => {
  if (!port.readable || !port.writable) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.usbStreamUnavailable',
      ),
    )
  }
}

const loadCertificateBytes = async (
  certificateUrl: string,
  onTerminalLog: OnTerminalLog,
  abortSignal: AbortSignal,
): Promise<Uint8Array> => {
  throwIfAborted(abortSignal)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.loadingCertificate',
    ),
  )
  const certificateResponse = await fetch(certificateUrl, { cache: 'no-cache' })
  throwIfAborted(abortSignal)
  if (!certificateResponse.ok) {
    throw new Error(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.errors.certificateLoadFailed',
      ),
    )
  }

  const certificateBuffer = await certificateResponse.arrayBuffer()
  throwIfAborted(abortSignal)
  const certificateBytes = new Uint8Array(certificateBuffer)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.certificateLoaded',
      { bytes: certificateBytes.byteLength },
    ),
  )
  return certificateBytes
}

export const uploadCertificate = async (
  port: SerialPortConnection,
  certificateUrl: string,
  usbConnection: UsbConnectionConfig,
  onSerialOutput: OnSerialOutput,
  onTerminalLog: OnTerminalLog,
  abortSignal: AbortSignal,
): Promise<void> => {
  throwIfAborted(abortSignal)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.preparingUploadFlow',
    ),
  )

  ensurePortStreamsAvailable(port)
  const certificateBytes = await loadCertificateBytes(
    certificateUrl,
    onTerminalLog,
    abortSignal,
  )
  throwIfAborted(abortSignal)
  onTerminalLog(
    i18n.t(
      'device.calibration.preFlightStep.certificateUploadFlow.logs.usingCurrentSerialConnection',
    ),
  )

  let activePort: SerialPortConnection = port
  let session = createSerialSession({
    activePort,
    onTerminalLog,
    onSerialOutput,
  })

  try {
    throwIfAborted(abortSignal)
    await switchToPassthroughMode(session, onTerminalLog)
    throwIfAborted(abortSignal)
    const reconnected = await reconnect(
      session,
      activePort,
      usbConnection,
      onTerminalLog,
      onSerialOutput,
      abortSignal,
    )
    session = reconnected.session
    activePort = reconnected.activePort
    throwIfAborted(abortSignal)
    await ensureModemReady(session, onTerminalLog)
    throwIfAborted(abortSignal)
    await deleteCertificates(session, onTerminalLog)
    throwIfAborted(abortSignal)
    await storeCertificate(session, certificateBytes, onTerminalLog)
    throwIfAborted(abortSignal)
    await checkModemAfterStore(session, onTerminalLog)
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.uploadFlowCompleted',
      ),
    )
  } finally {
    onTerminalLog(
      i18n.t(
        'device.calibration.preFlightStep.certificateUploadFlow.logs.cleaningSerialResources',
      ),
    )
    await closeSessionAndPort(session, activePort)
  }
}
